import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader } from "@/components/Loader";

const dur = { forever: "per sempre", once: "primo pagamento", repeating: "primi mesi" };

export default function AdminStripe() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/stripe-status").then(({ data }) => setS(data)).catch((e) => setErr(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const createCodes = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/stripe/create-codes");
      toast.success(data.created.length ? `Creati: ${data.created.join(", ")}` : "I codici esistono già");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (err) return <AppShell title="Stripe" greeting="Area admin"><p className="p-5 text-[#FCA5A5]">{err}</p></AppShell>;
  if (!s) return <Loader />;
  const live = s.mode === "live";
  const missing = ["COACH2027", "COACHVIP"].filter((k) => !s.codes.some((c) => c.active && c.code.toUpperCase() === k));
  const missingPrices = ["a2g_monthly", "a2g_annual"].filter((k) => !(s.prices || []).some((p) => p.lookup_key === k));
  const todo = [...missingPrices.map((k) => (k === "a2g_monthly" ? "prezzo mensile" : "prezzo annuale")), ...missing];
  return (
    <AppShell title="Stripe" greeting="Area admin">
      <div className="space-y-4 px-5 pt-5 pb-10" data-testid="admin-stripe">
        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <p className="text-sm text-[#8A8E96]">Account collegato all'app</p>
          <p className="mt-1 text-xl font-semibold text-[#FAF9F6]" data-testid="stripe-account-name">{s.account?.name || "—"}</p>
          <p className="text-sm text-[#71717A]">{s.account?.id}</p>
          <span data-testid="stripe-mode" className={`mt-3 inline-block rounded-full px-3 py-1 text-sm font-semibold ${live ? "bg-[#86EFAC]/12 text-[#86EFAC]" : "bg-[#FCD34D]/12 text-[#FCD34D]"}`}>
            {live ? "Modalità reale (pagamenti veri)" : "Modalità di prova"}
          </span>
          {s.error && <p className="mt-3 text-sm text-[#FCA5A5]">{s.error}</p>}
        </div>
        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <p className="font-semibold text-[#FAF9F6]">Codici sconto visti dall'app</p>
          {!s.codes.length && <p className="mt-2 text-sm text-[#FCA5A5]" data-testid="stripe-no-codes">Nessun codice in questo account e in questa modalità.</p>}
          <ul className="mt-3 space-y-2">
            {s.codes.map((c, i) => (
              <li key={i} className="flex items-center justify-between rounded-xl bg-[#09090B] px-3.5 py-2.5" data-testid={`stripe-code-${c.code}`}>
                <span className="flex items-center gap-2 font-semibold text-[#E5C158]">
                  {c.active ? <CheckCircle2 size={16} className="text-[#86EFAC]" /> : <XCircle size={16} className="text-[#71717A]" />} {c.code}
                </span>
                <span className="text-sm text-[#A1A1AA]">
                  {c.percent_off ? `-${c.percent_off}%` : ""} {dur[c.duration] || ""}{!c.active && " · disattivo"}
                  {c.expires_at ? ` · scade ${new Date(c.expires_at * 1000).toLocaleDateString("it-IT")}` : ""}
                </span>
              </li>
            ))}
          </ul>
          {todo.length > 0 && (
            <button onClick={createCodes} disabled={busy} data-testid="stripe-create-codes" className="btn-gold mt-4 flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm uppercase tracking-wide">
              {busy && <Loader2 size={15} className="animate-spin" />} Prepara l'account ({todo.join(", ")})
            </button>
          )}
          <p className="mt-3 text-[13px] text-[#8A8E96]" data-testid="stripe-prices">
            Prezzi: {(s.prices || []).length ? s.prices.map((p) => `€${(p.amount / 100).toFixed(2).replace(".", ",")}/${p.lookup_key === "a2g_annual" ? "anno" : "mese"}`).join(" · ") : "nessuno"}
          </p>
        </div>
      </div>
    </AppShell>
  );
}
